import Router from "next/router"
import { useEffect } from "react"

// Moves open tabs and the native-app WebViews onto a new deployment with no
// banner or prompt. Next's own stale-build handling only runs on a client-side
// navigation to a page with a data route, which most logged-in pages (chat,
// requests, booking, make-payment, account) don't have.
//
// The reload waits for a safe moment instead of firing when the deployment
// lands: booking state, chat drafts and signup steps live only in memory, and
// Checkout.js records a card payment without awaiting it, so a reload at the
// wrong time loses work or leaves a charge unrecorded. If a state here can't
// be seen by isBusy, put data-no-auto-reload on its root element.
//
// The version is the commit SHA, not the deployment id: the daily rating cron
// rebuilds through a deploy hook, which would otherwise reload everyone nightly
// for no code change. It is empty in local dev, which turns all of this off.
const BUILD_VERSION = process.env.REACT_APP_BUILD_VERSION

const CHECK_INTERVAL = 5 * 60 * 1000
// Longer than the slowest request a user can be waiting on (generate-diet-plan, 200s)
const IDLE_BEFORE_RELOAD = 5 * 60 * 1000
// Lets fire-and-forget calls made just before the navigation finish
const RELOAD_DELAY_AFTER_NAVIGATION = 1000

// Stripe Elements are iframes, so typing in them never reaches the dirty flag.
// The hooks.stripe.com frame is the 3DS challenge in Checkout.js.
const BUSY_SELECTOR = '.StripeElement, iframe[src*="hooks.stripe.com"], [data-no-auto-reload]'

const RELOAD_GUARD_KEY = "autoReloadedFor"
const CHUNK_ERROR = /ChunkLoadError|Loading (CSS )?chunk|Failed to load chunk/

// Once per tab per version, so a CDN or WebView still serving the old page
// cannot cause a reload loop. sessionStorage because UserContext clears
// localStorage; if it's unavailable the guard can't hold, so don't reload.
const reloadOnce = (version) => {
  try {
    if (window.sessionStorage.getItem(RELOAD_GUARD_KEY) === version) return
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, version)
  } catch {
    return
  }
  window.location.reload()
}

// A stale tab asking for a lazy chunk the new deployment no longer has.
// Anything else stays on the ErrorBoundary fallback.
export const reloadIfStaleChunk = (error) => {
  if (CHUNK_ERROR.test(`${error?.name} ${error?.message}`)) {
    reloadOnce(BUILD_VERSION)
  }
}

export const VersionWatcher = () => {
  useEffect(() => {
    if (!BUILD_VERSION) return

    let liveVersion = BUILD_VERSION
    let lastActivity = Date.now()
    let isFormDirty = false
    let currentPath = window.location.pathname
    let reloadTimer

    const isBusy = () => isFormDirty || document.querySelector(BUSY_SELECTOR) !== null

    const reloadIfSafe = () => {
      if (liveVersion !== BUILD_VERSION && !isBusy()) reloadOnce(liveVersion)
    }

    // Never on page load: first paint stays untouched and crawlers don't call it
    const check = async () => {
      try {
        const response = await fetch("/api/version", { cache: "no-store" })
        const { version } = await response.json()
        if (version) liveVersion = version
      } catch {
        return
      }
      if (Date.now() - lastActivity >= IDLE_BEFORE_RELOAD) reloadIfSafe()
    }

    const checkIfVisible = () => {
      if (document.visibilityState === "visible") check()
    }
    const onPageShow = (event) => {
      if (event.persisted) check()
    }
    const onActivity = () => {
      lastActivity = Date.now()
    }
    const onFormEdit = () => {
      isFormDirty = true
    }

    // Query-only and shallow changes keep the same page, and its form, mounted
    const onRouteChangeComplete = (url, { shallow } = {}) => {
      if (shallow || window.location.pathname === currentPath) return
      currentPath = window.location.pathname
      isFormDirty = false
      clearTimeout(reloadTimer)
      reloadTimer = setTimeout(reloadIfSafe, RELOAD_DELAY_AFTER_NAVIGATION)
    }

    const activityEvents = ["pointerdown", "keydown", "scroll", "touchstart"]
    const formEvents = ["input", "change"]
    const passiveCapture = { capture: true, passive: true }

    const interval = setInterval(checkIfVisible, CHECK_INTERVAL)
    document.addEventListener("visibilitychange", checkIfVisible)
    window.addEventListener("pageshow", onPageShow)
    activityEvents.forEach(name => document.addEventListener(name, onActivity, passiveCapture))
    formEvents.forEach(name => document.addEventListener(name, onFormEdit, true))
    Router.events.on("routeChangeComplete", onRouteChangeComplete)

    return () => {
      clearInterval(interval)
      clearTimeout(reloadTimer)
      document.removeEventListener("visibilitychange", checkIfVisible)
      window.removeEventListener("pageshow", onPageShow)
      activityEvents.forEach(name => document.removeEventListener(name, onActivity, passiveCapture))
      formEvents.forEach(name => document.removeEventListener(name, onFormEdit, true))
      Router.events.off("routeChangeComplete", onRouteChangeComplete)
    }
  }, [])

  return null
}
